import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { EthnicityType } from 'constants/loanApplication';
import {
  asianRaceOptions,
  demographicInformationProvisionTypeOptions,
  demographicSexOptions,
  ethnicityOptionsMap,
  hispanicOrLatinoEthnicityOptions,
  nativeHawaiianOrOtherRaceOptions,
} from 'constants/options';
import { ItemGroupAlignment } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { getCheckboxField } from 'utils/validation';

import { BorrowersTestIds } from '../../../../../../Borrowers.testIds';
import { defaultRadioInputProps, getDefaultCheckboxesGroupProps } from '../Declarations.constants';
import {
  getCheckboxStyles,
  getVerticalRadioListStyles,
  sectionStyles,
  sectionTitleStyles,
} from '../Declarations.styles';

import {
  checkboxInsteadOfRadioStyles,
  checkboxWrapperStyles,
  defaultCheckboxStylesProps,
  updatedTextFieldStyles,
} from './Demographic.styles';

export const getModelPath = entityId => `[${entityId}].declarations.demographicInformation.`;

export const getConfigFields = ({
  entityId,
  areRaceCheckboxesRequired,
  onChangeRaceInput,
  isFaceToFaceInterview,
  isSexInfoNotProvidedDuringFaceToFaceInterview,
  isEthnicityNotProvidedDuringFaceToFaceInterview,
  isSexSelected,
  isEthnicityRadioGroupSelected,
  onChangeSexFields,
  onChangeEthnicityFields,
  onChangeDemographicInformationProvisionType,
  isSomeOfEthnicityFieldsSelected,
}) => {
  const modelPath = getModelPath(entityId);

  const ethnicityTypeValidationSchema = (isRequired, validationMessage) =>
    getCheckboxField(isSomeOfEthnicityFieldsSelected || isRequired, validationMessage);

  return {
    ethnicityHispanicOrLatino: {
      fieldId: 'ethnicity',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}ethnicity.isHispanicOrLatino`,
      inputProps: {
        customOnChange: (e, value) => onChangeEthnicityFields(e, value, 'isHispanicOrLatino'),
        label: 'Ethnicity',
        ...defaultRadioInputProps,
        required: true,
        align: ItemGroupAlignment.VERTICAL,
        customStyles: getVerticalRadioListStyles(),
        options: [ethnicityOptionsMap[EthnicityType.HISPANIC_OR_LATINO]],
        noValidate: isFaceToFaceInterview && isEthnicityNotProvidedDuringFaceToFaceInterview,
        customSchema: ethnicityTypeValidationSchema,
      },
    },
    ethnicityHispanicOrLatinoOptions: {
      fieldId: 'ethnicity',
      type: FieldType.CHECKBOX_GROUP,
      entityName: 'ethnicity hispanic or latino',
      nameInModel: `${modelPath}ethnicity.hispanicOrLatinoOptions`,
      inputProps: {
        ...getDefaultCheckboxesGroupProps({
          padding: '0 0 10px 0',
          checkboxesPadding: '0 0 0 12px',
        }),
        isMulti: true,
        align: ItemGroupAlignment.VERTICAL,
        options: hispanicOrLatinoEthnicityOptions,
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_HISPANICORLATINOETHNICITYOPTIONS_INPUT,
        required: true,
        isRestyled: true,
      },
    },
    ethnicityOtherHispanicOrLatinoEthnicity: {
      fieldId: 'ethnicity',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}ethnicity.otherHispanicOrLatinoEthnicity`,
      inputProps: {
        placeholder: 'Type other ethnicity',
        title: '',
        label: 'Other Hispanic or Latino',
        customStyles: updatedTextFieldStyles,
        required: true,
        type: FieldInputType.TEXT,
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_OTHERHISPANICORLATINOETHNICITY_INPUT,
        'data-test-value': 'Other Hispanic',
      },
    },
    ethnicityNotHispanicOrLatino: {
      fieldId: 'ethnicity',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}ethnicity.isNonHispanicOrLatino`,
      inputProps: {
        ...defaultRadioInputProps,
        customOnChange: (e, value) => onChangeEthnicityFields(e, value, 'isNonHispanicOrLatino'),
        required: true,
        align: ItemGroupAlignment.VERTICAL,
        customStyles: getVerticalRadioListStyles(),
        customSchema: ethnicityTypeValidationSchema,
        options: [ethnicityOptionsMap[EthnicityType.NON_HISPANIC_OR_LATINO]],
        noValidate: isFaceToFaceInterview && isEthnicityNotProvidedDuringFaceToFaceInterview,
      },
    },
    ethnicityNotProvided: {
      fieldId: 'ethnicity',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}ethnicity.isEthnicityNotProvided`,
      inputProps: {
        ...defaultRadioInputProps,
        customOnChange: (e, value) => onChangeEthnicityFields(e, value, 'isEthnicityNotProvided'),
        required: true,
        align: ItemGroupAlignment.VERTICAL,
        customStyles: getVerticalRadioListStyles({ withBorderBottom: true, withHelperText: true }),
        customSchema: ethnicityTypeValidationSchema,
        options: [ethnicityOptionsMap[EthnicityType.NOT_PROVIDED]],
        noValidate: isFaceToFaceInterview && isEthnicityNotProvidedDuringFaceToFaceInterview,
      },
    },
    isEthnicityNotProvidedDuringFaceToFaceInterview: {
      fieldId: 'isEthnicityNotProvidedDuringFaceToFaceInterview',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}ethnicity.isEthnicityNotProvidedDuringFaceToFaceInterview`,
      inputProps: {
        customOnChange: (e, value) =>
          onChangeEthnicityFields(e, value, 'isEthnicityNotProvidedDuringFaceToFaceInterview'),
        required: !isEthnicityRadioGroupSelected,
        label: 'I do not wish to provide this information',
        'data-testid':
          BorrowersTestIds.DEMOGRAPHIC_FORM_ISETHNICITYNOTPROVIDEDDURINGFACETOFACEINTERVIEW_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles({
          withBottomBorder: true,
          withHelperText: true,
          customStyles: checkboxInsteadOfRadioStyles,
          ...defaultCheckboxStylesProps,
        }),
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    sex: {
      fieldId: 'sex',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}sex`,
      inputProps: {
        label: 'Sex',
        ...defaultRadioInputProps,
        align: ItemGroupAlignment.VERTICAL,
        radioGroupAlign: ItemGroupAlignment.VERTICAL,
        options: demographicSexOptions.slice(0, isFaceToFaceInterview ? -1 : Infinity),
        customOnChange: onChangeSexFields,
        customStyles: {
          ...getVerticalRadioListStyles({
            withBorderBottom: !isFaceToFaceInterview,
            withHelperText: !isFaceToFaceInterview,
          }),

          marginTop: '10px',
        },
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_SEX_SELECT,
        'data-test-value': 'Male',
        required: true,
        noValidate: isFaceToFaceInterview && isSexInfoNotProvidedDuringFaceToFaceInterview,
      },
    },
    isSexInfoNotProvidedDuringFaceToFaceInterview: {
      fieldId: 'isSexInfoNotProvidedDuringFaceToFaceInterview',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}isSexInfoNotProvidedDuringFaceToFaceInterview`,
      inputProps: {
        customOnChange: onChangeSexFields,
        required: !isSexSelected,
        label: 'I do not wish to provide this information',
        'data-testid':
          BorrowersTestIds.DEMOGRAPHIC_FORM_ISSEXINFONOTPROVIDEDDURINGFACETOFACEINTERVIEW_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles({
          withBottomBorder: true,
          withHelperText: true,
          customStyles: checkboxInsteadOfRadioStyles,
          ...defaultCheckboxStylesProps,
        }),
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    raceLabel: {
      fieldId: 'race',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}race.raceLabel`,
      inputProps: {
        label: 'Race',
        ...defaultRadioInputProps,
        customStyles: { ...getVerticalRadioListStyles(), marginTop: '10px' },
        options: null,
        required: true,
        noValidate: !areRaceCheckboxesRequired,
      },
    },
    raceIsAmericanIndianOrAlaskaNative: {
      fieldId: 'race',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}race.isAmericanIndianOrAlaskaNative`,
      inputProps: {
        label: 'American Indian or Alaska Native',
        required: areRaceCheckboxesRequired,
        'data-testid': BorrowersTestIds.DEMOGRAPHIC_FORM_ISAMERICANINDIANORALASKANATIVE_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles(defaultCheckboxStylesProps),
        customOnChange: onChangeRaceInput,
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    americanTribeName: {
      fieldId: 'race',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}race.americanTribeName`,
      inputProps: {
        placeholder: 'Print name of enrolled or principal tribe',
        label: 'American Indian or Alaska Native',
        customStyles: updatedTextFieldStyles,
        required: true,
        type: FieldInputType.TEXT,
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_AMERICANTRIBENAME_INPUT,
        'data-test-value': 'American Tribe Name',
      },
    },
    raceIsAsian: {
      fieldId: 'race',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}race.isAsian`,
      inputProps: {
        label: 'Asian',
        required: areRaceCheckboxesRequired,
        'data-testid': BorrowersTestIds.DEMOGRAPHIC_FORM_ISASIAN_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles(defaultCheckboxStylesProps),
        customOnChange: onChangeRaceInput,
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    raceAsianOptions: {
      fieldId: 'race',
      type: FieldType.CHECKBOX_GROUP,
      entityName: 'race asian',
      nameInModel: `${modelPath}race.asianOptions`,
      inputProps: {
        ...getDefaultCheckboxesGroupProps({
          padding: '0 0 10px 0',
          checkboxesPadding: '0 0 0 12px',
        }),
        isMulti: true,
        align: ItemGroupAlignment.VERTICAL,
        options: asianRaceOptions,
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_ASIANOPTIONS_CHECKBOX,
        required: true,
        isRestyled: true,
      },
    },
    raceOtherAsian: {
      fieldId: 'race',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}race.otherAsianRace`,
      inputProps: {
        placeholder: 'Print Race',
        label: 'Other Asian',
        customStyles: updatedTextFieldStyles,
        required: true,
        type: FieldInputType.TEXT,
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_OTHERASIANRACE_INPUT,
        'data-test-value': 'Other Asian Race',
      },
    },
    raceIsBlackOrAfricanAmerican: {
      fieldId: 'race',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}race.isBlackOrAfricanAmerican`,
      inputProps: {
        label: 'Black or African American',
        required: areRaceCheckboxesRequired,
        'data-testid': BorrowersTestIds.DEMOGRAPHIC_FORM_ISBLACKORAFRICANAMERICAN_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles(defaultCheckboxStylesProps),
        customOnChange: onChangeRaceInput,
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    raceIsNativeHawaiianOrOtherPacificIslander: {
      fieldId: 'race',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}race.isNativeHawaiianOrOtherPacificIslander`,
      inputProps: {
        label: 'Native Hawaiian or Other Pacific Islander',
        required: areRaceCheckboxesRequired,
        'data-testid':
          BorrowersTestIds.DEMOGRAPHIC_FORM_ISNATIVEHAWAIIANOROTHERPACIFICISLANDER_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles(defaultCheckboxStylesProps),
        customOnChange: onChangeRaceInput,
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    raceNativeHawaiianOrOtherPacificIslanderOptions: {
      fieldId: 'race',
      type: FieldType.CHECKBOX_GROUP,
      nameInModel: `${modelPath}race.nativeHawaiianOrOtherPacificIslanderOptions`,
      inputProps: {
        ...getDefaultCheckboxesGroupProps({
          padding: '0 0 10px 0',
          checkboxesPadding: '0 0 0 12px',
        }),
        isMulti: true,
        align: ItemGroupAlignment.VERTICAL,
        options: nativeHawaiianOrOtherRaceOptions,
        dataTestId:
          BorrowersTestIds.DEMOGRAPHIC_FORM_NATIVEHAWAIIANOROTHERPACIFICISLANDEROPTIONS_CHECKBOX,
        required: true,
        isRestyled: true,
      },
    },
    raceOtherPacificIslander: {
      fieldId: 'race',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}race.otherPacificIslander`,
      inputProps: {
        placeholder: 'Print Race',
        label: 'Other Pacific Islander',
        customStyles: updatedTextFieldStyles,
        required: true,
        type: FieldInputType.TEXT,
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_OTHERPACIFICISLANDER_INPUT,
        'data-test-value': 'Other Pacific Islander',
      },
    },
    raceIsWhite: {
      fieldId: 'race',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}race.isWhite`,
      inputProps: {
        label: 'White',
        required: areRaceCheckboxesRequired,
        'data-testid': BorrowersTestIds.DEMOGRAPHIC_FORM_ISWHITE_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles(defaultCheckboxStylesProps),
        customOnChange: onChangeRaceInput,
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    raceIsRaceNotProvided: {
      fieldId: 'race',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}race.isRaceNotProvided`,
      inputProps: {
        label: 'I do not wish to provide this information',
        required: areRaceCheckboxesRequired,
        'data-testid': BorrowersTestIds.DEMOGRAPHIC_FORM_ISNOTPROVIDED_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles({
          withHelperText: true,
          withBottomBorder: true,
          ...defaultCheckboxStylesProps,
        }),
        customOnChange: onChangeRaceInput,
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    isRaceNotProvidedDuringFaceToFaceInterview: {
      fieldId: 'isRaceNotProvidedDuringFaceToFaceInterview',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}race.isRaceNotProvidedDuringFaceToFaceInterview`,
      inputProps: {
        label: 'I do not wish to provide this information',
        required: areRaceCheckboxesRequired,
        'data-testid':
          BorrowersTestIds.DEMOGRAPHIC_FORM_ISRACENOTPROVIDEDDURINGFACETOFACEINTERVIEW_CHECKBOX,
        'data-test-value': true,
        customStyles: getCheckboxStyles({
          withHelperText: true,
          withBottomBorder: true,
          ...defaultCheckboxStylesProps,
        }),
        customOnChange: onChangeRaceInput,
        wrapperStyles: checkboxWrapperStyles,
      },
    },
    demographicInformationProvisionType: {
      fieldId: 'demographicInformationProvisionType',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}demographicInformationProvisionType`,
      inputProps: {
        label: 'The Demographic Information was provided through',
        ...defaultRadioInputProps,
        customOnChange: onChangeDemographicInformationProvisionType,
        align: ItemGroupAlignment.VERTICAL,
        radioGroupAlign: ItemGroupAlignment.VERTICAL,
        options: demographicInformationProvisionTypeOptions,
        dataTestId: BorrowersTestIds.DEMOGRAPHIC_FORM_DEMOGRAPHIC_INFORMATION_PROVISION_TYPE_SELECT,
        'data-test-value': 'EmailOrInternet',
        required: true,
        customStyles: {
          ...getVerticalRadioListStyles({ withHelperText: true, withBorderBottom: true }),
          marginTop: '10px',
        },
      },
    },
    ethnicityCollectedOnTheBasisOfVisualObservationOrSurname: {
      fieldId: 'ethnicityCollectedOnTheBasisOfVisualObservationOrSurname',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}ethnicityCollectedOnTheBasisOfVisualObservationOrSurname`,
      inputProps: {
        label:
          'Was the ethnicity of the Borrower collected on the basis of visual observation or surname?',
        dataTestId:
          BorrowersTestIds.DEMOGRAPHIC_FORM_ETHNICITY_COLLECTED_ON_THE_BASIS_OF_VISUAL_OBSERVATION_OR_SURNAME_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    sexCollectedOnTheBasisOfVisualObservationOrSurname: {
      fieldId: 'sexCollectedOnTheBasisOfVisualObservationOrSurname',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}sexCollectedOnTheBasisOfVisualObservationOrSurname`,
      inputProps: {
        label:
          'Was the sex of the Borrower collected on the basis of visual observation or surname?',
        dataTestId:
          BorrowersTestIds.DEMOGRAPHIC_FORM_SEX_COLLECTED_ON_THE_BASIS_OF_VISUAL_OBSERVATION_OR_SURNAME_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    raceCollectedOnTheBasisOfVisualObservationOrSurname: {
      fieldId: 'raceCollectedOnTheBasisOfVisualObservationOrSurname',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}raceCollectedOnTheBasisOfVisualObservationOrSurname`,
      inputProps: {
        label:
          'Was the race of the Borrower collected on the basis of visual observation or surname?',
        dataTestId:
          BorrowersTestIds.DEMOGRAPHIC_FORM_RACE_COLLECTED_ON_THE_BASIS_OF_VISUAL_OBSERVATION_OR_SURNAME_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
  };
};

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    isHispanicOrLatino,
    isOtherHispanicOrLatinoEthnicity,
    isAmericanIndianOrAlaskaNative,
    isAsian,
    isOtherAsian,
    isNativeHawaiianOrOtherPacificIslander,
    isOtherPacificIslander,
    areRaceCheckboxesRequired,
    onChangeRaceInput,
    isFaceToFaceInterview,
    isSexInfoNotProvidedDuringFaceToFaceInterview,
    isEthnicityNotProvidedDuringFaceToFaceInterview,
    isSexSelected,
    isEthnicityRadioGroupSelected,
    onChangeSexFields,
    onChangeEthnicityFields,
    onChangeDemographicInformationProvisionType,
    isSomeOfEthnicityFieldsSelected,
  }) => {
    const configFields = getConfigFields({
      entityId,
      areRaceCheckboxesRequired,
      onChangeRaceInput,
      isFaceToFaceInterview,
      isSexInfoNotProvidedDuringFaceToFaceInterview,
      isEthnicityNotProvidedDuringFaceToFaceInterview,
      isSexSelected,
      isEthnicityRadioGroupSelected,
      onChangeSexFields,
      onChangeEthnicityFields,
      onChangeDemographicInformationProvisionType,
      isSomeOfEthnicityFieldsSelected,
    });

    return {
      groups: [
        {
          title: {
            label: 'Demographic',
            titleStyles: sectionTitleStyles,
          },
          key: `${entityId}-declarations-demographic-information`,
          customStyles: { ...sectionStyles, gap: '8px' },
          type: FieldType.GROUP,
          fields: [
            configFields.ethnicityHispanicOrLatino,
            ...getConditionalArray(isHispanicOrLatino, () => [
              configFields.ethnicityHispanicOrLatinoOptions,

              ...getConditionalArray(isOtherHispanicOrLatinoEthnicity, [
                configFields.ethnicityOtherHispanicOrLatinoEthnicity,
              ]),
            ]),

            configFields.ethnicityNotHispanicOrLatino,
            ...(isFaceToFaceInterview
              ? [configFields.isEthnicityNotProvidedDuringFaceToFaceInterview]
              : [configFields.ethnicityNotProvided]),

            configFields.sex,
            ...getConditionalArray(isFaceToFaceInterview, [
              configFields.isSexInfoNotProvidedDuringFaceToFaceInterview,
            ]),

            configFields.raceLabel,
            configFields.raceIsAmericanIndianOrAlaskaNative,
            ...getConditionalArray(isAmericanIndianOrAlaskaNative, [
              configFields.americanTribeName,
            ]),

            configFields.raceIsAsian,
            ...getConditionalArray(isAsian, () => [
              configFields.raceAsianOptions,

              ...getConditionalArray(isOtherAsian, [configFields.raceOtherAsian]),
            ]),

            configFields.raceIsBlackOrAfricanAmerican,
            configFields.raceIsNativeHawaiianOrOtherPacificIslander,
            ...getConditionalArray(isNativeHawaiianOrOtherPacificIslander, () => [
              configFields.raceNativeHawaiianOrOtherPacificIslanderOptions,

              ...getConditionalArray(isOtherPacificIslander, [
                configFields.raceOtherPacificIslander,
              ]),
            ]),

            configFields.raceIsWhite,
            ...(isFaceToFaceInterview
              ? [configFields.isRaceNotProvidedDuringFaceToFaceInterview]
              : [configFields.raceIsRaceNotProvided]),

            configFields.demographicInformationProvisionType,
            ...getConditionalArray(isFaceToFaceInterview, [
              configFields.ethnicityCollectedOnTheBasisOfVisualObservationOrSurname,
              configFields.sexCollectedOnTheBasisOfVisualObservationOrSurname,
              configFields.raceCollectedOnTheBasisOfVisualObservationOrSurname,
            ]),
          ],
        },
      ],
    };
  },
);
